import { FormEvent, useState } from 'react';

import { type ApiFoundPropertyProject, FIND_PROPERTY_PROJECTS_MAX_QUERIES, type PropertyProjectMatch } from '@kaiizen/kai-pro-api';
import { useFindPropertyProjects, usePurchaseOrderExcelDownloads } from '@kaiizen/kai-pro-api-react-state';

import { downloadBlob } from '@/Client/Lib';
import { CommonActionButton } from '@kaiizen/kai-shiki/react';

import { buildPurchaseOrdersZip, NOT_INCLUDED_CSV_FILENAME, PurchaseOrderLeftOut } from './purchaseOrdersZipLib';
import { getErrorMessage } from './superAdminToolsLib';
import { DEFAULT_SUPER_ADMIN_TOOLS_PANEL_STYLES, SuperAdminToolsPanelStyles } from './superAdminToolsPanelStyles';
import { SuperAdminToolsRow, SuperAdminToolsRows } from './SuperAdminToolsRows';

const MATCH_LABELS: Record<PropertyProjectMatch, string> = {
  projectId: 'project ID',
  propertyId: 'property ID',
  customerPropertyId: 'customer property ID',
  address: 'address',
  partialAddress: 'partial address',
};

type ScanTarget = { query: string; address?: string; scanId: string };

/** One query per non-blank line, without repeats. */
const getQueries = (queriesText: string) => [...new Set(queriesText.split('\n').map(line => line.trim()).filter(Boolean))];

const getScanTarget = ({ query, orgProperty, propertyProject }: ApiFoundPropertyProject): ScanTarget | undefined =>
  propertyProject?.latestScanId ? { query, address: orgProperty?.address, scanId: propertyProject.latestScanId } : undefined;

const getLeftOutReason = ({ orgProperty, propertyProject, error }: ApiFoundPropertyProject) =>
  error ?? `${propertyProject?.name ?? orgProperty?.address} has no scope`;

/** The latest scan of each found project (once each, though several queries may find it), and every other query with why it is left out. */
const getExportPlan = (results: ApiFoundPropertyProject[]) => {
  const scanTargets = results.flatMap(result => getScanTarget(result) ?? []);
  return {
    scanTargets,
    scanIds: [...new Set(scanTargets.map(({ scanId }) => scanId))],
    leftOut: results.flatMap((result): PurchaseOrderLeftOut[] =>
      getScanTarget(result) ? [] : [{ query: result.query, address: result.orgProperty?.address, reason: getLeftOutReason(result) }]
    ),
  };
};

const getResultRow = (result: ApiFoundPropertyProject, styles: SuperAdminToolsPanelStyles): SuperAdminToolsRow => ({
  label: result.query,
  value: getScanTarget(result)
    ? `${result.orgProperty?.address}: ${result.propertyProject?.name} (matched by ${MATCH_LABELS[result.matchedBy!]})`
    : <span css={styles.error}>{getLeftOutReason(result)}</span>,
});

const getTodayDate = () => new Date().toISOString().slice(0, 10);

/**
 * Super admin download of many purchase orders from the current org as one zip. Each line names a property or project by
 * address, customer property ID, property ID, or project ID; the matches are listed before anything downloads.
 */
export const SuperAdminBulkExport = ({ styles = DEFAULT_SUPER_ADMIN_TOOLS_PANEL_STYLES }: { styles?: SuperAdminToolsPanelStyles }) => {
  const [queriesText, setQueriesText] = useState('');
  const [results, setResults] = useState<ApiFoundPropertyProject[]>();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadedMessage, setDownloadedMessage] = useState<string>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const findPropertyProjects = useFindPropertyProjects();
  const { download, progress } = usePurchaseOrderExcelDownloads();

  const queries = getQueries(queriesText);
  const hasTooManyQueries = queries.length > FIND_PROPERTY_PROJECTS_MAX_QUERIES;
  const exportPlan = results && getExportPlan(results);

  const editQueries = (text: string) => {
    setQueriesText(text);
    setResults(undefined);
    setDownloadedMessage(undefined);
  };

  const find = async (event: FormEvent) => {
    event.preventDefault();
    setErrorMessage(undefined);
    setDownloadedMessage(undefined);
    try {
      setResults(await findPropertyProjects.mutate({ queries }));
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  };

  const downloadPurchaseOrders = async ({ scanTargets, scanIds, leftOut }: ReturnType<typeof getExportPlan>) => {
    setErrorMessage(undefined);
    setIsDownloading(true);
    try {
      const { files, failures } = await download(scanIds);
      const failedLeftOut = failures.flatMap(({ scanId, error }) =>
        scanTargets.filter(target => target.scanId === scanId).map(({ query, address }) => ({ query, address, reason: error }))
      );
      const allLeftOut = [...leftOut, ...failedLeftOut];
      downloadBlob(buildPurchaseOrdersZip(files, allLeftOut), `purchase-orders-${getTodayDate()}.zip`);
      setDownloadedMessage(
        `Downloaded ${files.length} purchase orders${allLeftOut.length ? `; ${allLeftOut.length} not included, listed in ${NOT_INCLUDED_CSV_FILENAME}` : ''}`,
      );
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <form css={styles.panel} onSubmit={find}>
      <div css={styles.heading}>{'Bulk Export'}</div>
      <div css={styles.description}>
        {"Downloads purchase orders for many of this org's properties as one zip. Enter one per line: an address, customer property ID, property ID, or project ID."}
      </div>
      <textarea
        css={styles.textArea}
        aria-label='Properties to export'
        placeholder={'123 Main St, Atlanta, GA 30318\nAMH-00123'}
        value={queriesText}
        onChange={event => editQueries(event.target.value)}
      />
      {hasTooManyQueries && <div css={styles.error}>{`At most ${FIND_PROPERTY_PROJECTS_MAX_QUERIES} at a time; this list has ${queries.length}.`}</div>}
      <div css={styles.actions}>
        <CommonActionButton type='submit' disabled={!queries.length || hasTooManyQueries || findPropertyProjects.isPending}>
          {findPropertyProjects.isPending ? 'Finding...' : 'Find'}
        </CommonActionButton>
      </div>
      {errorMessage && <div css={styles.error}>{errorMessage}</div>}
      {results && exportPlan && (
        <>
          <div css={styles.description}>{`${exportPlan.scanTargets.length} of ${results.length} found with a scope`}</div>
          <SuperAdminToolsRows rows={results.map(result => getResultRow(result, styles))} styles={styles} />
          <div css={styles.actions}>
            <CommonActionButton onClick={() => downloadPurchaseOrders(exportPlan)} disabled={!exportPlan.scanIds.length || isDownloading}>
              {isDownloading && progress ? `Downloading ${progress.done} of ${progress.total}...` : `Download ${exportPlan.scanIds.length} purchase orders`}
            </CommonActionButton>
          </div>
        </>
      )}
      {downloadedMessage && <div css={styles.success}>{downloadedMessage}</div>}
    </form>
  );
};
